import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NewSiteNav, NewSiteFooter } from "@/components/new-site/NewSiteChrome";
import TrialRequestForm from "@/components/TrialRequestForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import "@/components/new-site/mobile-openings.css";
import { MapPin, Mail } from "lucide-react";

/** Scroll to the top on route change, or to the #section a link asked for. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
      return;
    }
    // The target page may still be mounting, so retry until its section is in the DOM.
    const id = decodeURIComponent(hash.slice(1));
    let attempts = 0;
    let frame = 0;
    const jump = () => {
      const target = document.getElementById(id);
      if (target?.closest(".ns-mobile-carousel")) {
        // Embla controls horizontal positioning; native scrollIntoView would shift its viewport too.
        const navHeight = document.querySelector(".ns-workspace > header")?.getBoundingClientRect().height ?? 72;
        window.scrollTo({ top: window.scrollY + target.getBoundingClientRect().top - navHeight - 16, behavior: "instant" as ScrollBehavior });
      }
      else if (target) target.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "start" });
      else if (++attempts < 30) frame = requestAnimationFrame(jump);
    };
    jump();
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
  return null;
}

/** Private (superuser-only) workspace for the redesigned public website. */
export default function NewSiteLayout({
  children,
}: {
  children: ReactNode | ((openContact: () => void) => ReactNode);
}) {
  const [contactOpen, setContactOpen] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const openContact = () => setContactOpen(true);

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    const banner = workspace.querySelector(":scope > .ns-preview-banner");
    const nav = workspace.querySelector(":scope > header");
    const opening = workspace.querySelector(".ns-about-mission, .ns-solution-hero, .ns-sectors > section:first-of-type, .ns-cases:not(.ns-cases-article) > section:first-of-type, .ns-news-article, .ns-cases-article");
    const copy = opening?.matches("article")
      ? opening.querySelector(":scope > header")
      : opening?.querySelector(":scope > div");
    const measure = () => {
      workspace.style.setProperty("--ns-chrome-height", `${(banner?.getBoundingClientRect().height ?? 0) + (nav?.getBoundingClientRect().height ?? 0)}px`);
      if (opening instanceof HTMLElement && copy) {
        opening.style.setProperty("--ns-opening-copy-height", `${copy.getBoundingClientRect().height}px`);
      }
    };
    const observer = new ResizeObserver(measure);
    [banner, nav, copy].forEach((element) => { if (element) observer.observe(element); });
    measure();
    return () => observer.disconnect();
  }, [pathname]);

  return (
    <div ref={workspaceRef} className="ns-workspace min-h-screen bg-ns-white text-ns-plum font-archivo text-left">
      <ScrollToTop />
      <NewSiteNav onContact={openContact} />
      <main>{typeof children === "function" ? children(openContact) : children}</main>
      <NewSiteFooter onContact={openContact} />
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contact Us</DialogTitle>
            <DialogDescription>Get in touch directly, or fill in your details and we'll get back to you.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 rounded-lg border-l-4 border-ns-lime bg-ns-beige/60 p-4 text-sm text-ns-plum">
            <a
              href="https://www.google.com/maps/search/?api=1&query=85+Explorations+Drive+Leicester+LE4+5NU"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 hover:underline"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Snapshot Live Ltd, DOCK 2/006, 85 Explorations Drive, Leicester, LE4 5NU, United Kingdom</span>
            </a>
            <a href="mailto:info@snapshot.live" className="flex items-center gap-3 hover:underline">
              <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>info@snapshot.live</span>
            </a>
          </div>
          <p className="text-sm font-medium text-ns-plum">Or send us a message:</p>
          <TrialRequestForm onOpenChange={setContactOpen} defaultInquiryType="brochure_request" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
